import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Billing() {
  const { activeCompany, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [p, s] = await Promise.all([
      api.get("/billing/plans").then((r) => r.data),
      api.get("/billing/subscription").then((r) => r.data),
    ]);
    setPlans(p);
    setSub(s);
  }, []);
  useEffect(() => { load(); }, [activeCompany?.id, load]);

  const [portalLoading, setPortalLoading] = useState(false);
  const openPortal = async () => {
    setError(""); setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: `${window.location.origin}/app/billing`,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setPortalLoading(false);
    }
  };

  // Poll status if returning from Stripe
  const [paymentStatus, setPaymentStatus] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    setPaymentStatus("processing");
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaymentStatus("paid");
          await refresh();
          await load();
          setTimeout(() => navigate("/app/billing", { replace: true }), 1500);
          return;
        }
        if (data.status === "expired") {
          setPaymentStatus("expired");
          setTimeout(() => navigate("/app/billing", { replace: true }), 1500);
          return;
        }
      } catch (e) {
        // Transient (5xx, Stripe proxy lag) — keep polling
        console.warn("billing status poll error", e?.response?.status || e?.message);
      }
      if (attempts >= 20) {
        // ~60s total; give up so user can retry
        setPaymentStatus("timeout");
        return;
      }
      if (!cancelled) setTimeout(poll, 3000);
    };
    poll();
    return () => { cancelled = true; };
  }, [location.search, navigate, refresh, load]);

  const checkout = async (planId) => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  const isCurrent = (planId) => activeCompany?.plan === planId;

  return (
    <div data-testid="billing-page">
      <div className="mb-6">
        <div className="label-meta">Workspace</div>
        <div className="display">Billing & plans</div>
        <p className="text-[13px] mt-1" style={{ color: "var(--parapet-text-secondary)" }}>
          Current plan: <span className="value-mono">{(activeCompany?.plan || "standard").toUpperCase()}</span>
        </p>
      </div>

      {/* Manage subscription panel — visible once a Stripe customer exists */}
      {sub?.subscription?.stripe_customer_id && (
        <div className="p-card mb-6 flex items-start justify-between" data-testid="manage-subscription-card">
          <div>
            <div className="label-meta">Subscription</div>
            <div className="text-[13px] mt-1">
              Status: <span className="value-mono">{sub.subscription.status || "active"}</span>
              {sub.subscription.cancel_at_period_end && (
                <span className="ml-2" style={{ color: "#7a0606" }}>· cancels at end of period</span>
              )}
            </div>
            {sub.subscription.current_period_end && (
              <div className="text-[12px] mt-1" style={{ color: "var(--parapet-text-secondary)" }}>
                {sub.subscription.cancel_at_period_end ? "Access ends" : "Renews"} on{" "}
                {new Date(sub.subscription.current_period_end * 1000).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
              </div>
            )}
          </div>
          <button
            className="btn-secondary"
            onClick={openPortal}
            disabled={portalLoading}
            data-testid="manage-subscription-button"
          >
            {portalLoading ? "Opening…" : "Manage subscription"}
          </button>
        </div>
      )}
      {error && <div className="text-[12px] p-2 border mb-4" style={{ borderColor: "#111" }}>{error}</div>}

      {paymentStatus && (
        <div
          className="p-3 border mb-4 flex items-center gap-2 text-[13px]"
          style={{
            borderColor: paymentStatus === "paid" ? "#0a6b2a" : paymentStatus === "timeout" || paymentStatus === "expired" ? "#7a0606" : "#111",
            background: paymentStatus === "paid" ? "#E7F5EB" : paymentStatus === "timeout" || paymentStatus === "expired" ? "#FDECEC" : "#F4F4F4",
          }}
          data-testid="payment-banner"
        >
          {paymentStatus === "processing" && <><span className="spinner" /> Confirming payment with Stripe…</>}
          {paymentStatus === "paid" && <>Payment confirmed — your plan is being upgraded.</>}
          {paymentStatus === "expired" && <>Checkout session expired. Please try again.</>}
          {paymentStatus === "timeout" && (
            <>Payment confirmation is taking longer than expected. Refresh in a moment; if your plan hasn't updated, click <button className="underline ml-1" onClick={() => window.location.reload()}>retry</button>.</>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 max-w-md">
        {plans.map((p) => (
          <div key={p.id} className="p-card flex flex-col" data-testid={`plan-card-${p.id}`}
               style={{ background: isCurrent(p.id) ? "#111" : "#fff", color: isCurrent(p.id) ? "#fff" : "#111" }}>
            <div className="label-meta" style={{ color: isCurrent(p.id) ? "#999" : "var(--parapet-text-secondary)" }}>{p.name}</div>
            <div className="display" style={{ fontSize: 36, marginTop: 8 }}>
              £{p.price_gbp.toFixed(0)}
              <span className="text-[13px] font-normal" style={{ color: isCurrent(p.id) ? "#999" : "#888" }}>/mo</span>
            </div>
            <div
              className="text-[13px] mt-4 flex-1"
              style={{ color: isCurrent(p.id) ? "#ccc" : "var(--parapet-text-secondary)", lineHeight: 1.55 }}
            >
              Full access to every Parapet workspace, source feed and analysis tool. No tiers, no add-ons.
            </div>
            <div className="mt-4">
              {isCurrent(p.id) ? (
                <button className="btn-secondary w-full" disabled data-testid={`plan-current-${p.id}`}>Current plan</button>
              ) : (
                <button className="btn-primary w-full" onClick={() => checkout(p.id)} disabled={loading} data-testid={`plan-select-${p.id}`}>
                  {loading ? "Redirecting…" : "Subscribe"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {sub?.subscription && (
        <div className="p-card mt-6">
          <h2 className="mb-2">Subscription details</h2>
          <div className="text-[13px] space-y-1">
            <div>Plan: <span className="value-mono">{sub.subscription.plan}</span></div>
            <div>Status: <span className="value-mono">{sub.subscription.status}</span></div>
            <div style={{ color: "var(--parapet-text-secondary)" }}>Activated: {sub.subscription.activated_at}</div>
          </div>
        </div>
      )}
    </div>
  );
}
